import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../lib/api.ts';
import { inkLine, pointAt } from '../lib/inkGlyphs.ts';
import {
  clearStoredSketch,
  loadSketch,
  saveSketch,
  type SketchPoint,
  type SketchStroke,
} from '../lib/sketchStore.ts';
import './sketch-pad.css';

export const SKETCH_TUTORIAL_SCRIPT =
  'Watch the pencil write x plus two equals five. This is just an example. Try your own equation on one line below. Then choose Preview, check the digital equation, fix anything I misread, and choose Use this equation.';
const demoInk = inkLine('x + 2 = 5');

type Recognition = {
  equation: string;
  confidence: null;
  provider: string;
  limitations: string;
};

export interface SketchPadProps {
  activityKey: string;
  onAcceptedEquation: (equation: string) => void;
  onTutorialRequested?: (script: string) => void;
  tutorialPosition?: 'above' | 'inside';
  tutorialAudioPositionMs?: number;
  tutorialAudioDurationMs?: number;
  onTutorialDismiss?: () => void;
  className?: string;
}

const tutorialKey = 'enactry-sketch-tutorial-uses-v1';
const readTutorialUses = (): number => {
  try {
    return Number(localStorage.getItem(tutorialKey) || 0);
  } catch {
    return 0;
  }
};

const draw = (canvas: HTMLCanvasElement, strokes: SketchStroke[]): void => {
  const context = canvas.getContext('2d');
  if (!context) return;
  const ratio = window.devicePixelRatio || 1;
  const width = canvas.clientWidth;
  const height = canvas.clientHeight;
  if (
    canvas.width !== Math.round(width * ratio) ||
    canvas.height !== Math.round(height * ratio)
  ) {
    canvas.width = Math.round(width * ratio);
    canvas.height = Math.round(height * ratio);
  }
  context.setTransform(ratio, 0, 0, ratio, 0, 0);
  context.fillStyle = '#fffefa';
  context.fillRect(0, 0, width, height);
  context.strokeStyle = '#173f38';
  context.lineWidth = 5;
  context.lineCap = 'round';
  context.lineJoin = 'round';
  for (const stroke of strokes) {
    if (!stroke.length) continue;
    context.beginPath();
    context.moveTo(stroke[0].x * width, stroke[0].y * height);
    for (const point of stroke.slice(1))
      context.lineTo(point.x * width, point.y * height);
    context.stroke();
  }
};

export function SketchPad({
  activityKey,
  onAcceptedEquation,
  onTutorialRequested,
  tutorialPosition = 'above',
  tutorialAudioPositionMs,
  tutorialAudioDurationMs,
  onTutorialDismiss,
  className = '',
}: SketchPadProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [strokes, setStrokes] = useState<SketchStroke[]>(() =>
    loadSketch(activityKey),
  );
  const strokesRef = useRef(strokes);
  const storageKeyRef = useRef(activityKey);
  const skipSaveRef = useRef(false);
  const drawingRef = useRef(false);
  const recognitionGenerationRef = useRef(0);
  const recognitionAbortRef = useRef<AbortController | undefined>(undefined);
  const tutorialCallbackRef = useRef(onTutorialRequested);
  const tutorialStartedRef = useRef(new Set<string>());
  const [recognition, setRecognition] = useState<Recognition>();
  const [draft, setDraft] = useState('');
  const [status, setStatus] = useState('');
  const [busy, setBusy] = useState(false);
  const previewRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (recognition)
      requestAnimationFrame(() => {
        previewRef.current?.scrollIntoView({
          block: 'nearest',
          behavior: 'instant',
        });
        previewRef.current
          ?.querySelector('input')
          ?.focus({ preventScroll: true });
      });
  }, [recognition]);
  const [showTutorial, setShowTutorial] = useState(
    () => readTutorialUses() < 3,
  );

  useEffect(() => {
    recognitionGenerationRef.current += 1;
    recognitionAbortRef.current?.abort();
    const restored = loadSketch(activityKey);
    storageKeyRef.current = activityKey;
    skipSaveRef.current = true;
    strokesRef.current = restored;
    setStrokes(restored);
    setRecognition(undefined);
    setDraft('');
    setShowTutorial(readTutorialUses() < 3);
  }, [activityKey]);

  useEffect(() => {
    strokesRef.current = strokes;
    if (skipSaveRef.current) skipSaveRef.current = false;
    else saveSketch(storageKeyRef.current, strokes);
    if (canvasRef.current) draw(canvasRef.current, strokes);
  }, [activityKey, strokes]);

  useEffect(() => {
    tutorialCallbackRef.current = onTutorialRequested;
  }, [onTutorialRequested]);

  useEffect(() => {
    if (!showTutorial || tutorialStartedRef.current.has(activityKey)) return;
    tutorialStartedRef.current.add(activityKey);
    tutorialCallbackRef.current?.(SKETCH_TUTORIAL_SCRIPT);
    const next = Math.min(3, readTutorialUses() + 1);
    try {
      localStorage.setItem(tutorialKey, String(next));
    } catch {}
  }, [activityKey, showTutorial]);

  useEffect(
    () => () => {
      recognitionGenerationRef.current += 1;
      recognitionAbortRef.current?.abort();
    },
    [],
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const observer = new ResizeObserver(() => draw(canvas, strokesRef.current));
    observer.observe(canvas);
    draw(canvas, strokesRef.current);
    return () => observer.disconnect();
  }, []);

  const pointFor = (
    event: React.PointerEvent<HTMLCanvasElement>,
  ): SketchPoint => {
    const bounds = event.currentTarget.getBoundingClientRect();
    return {
      x: (event.clientX - bounds.left) / bounds.width,
      y: (event.clientY - bounds.top) / bounds.height,
    };
  };
  const begin = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const point = pointFor(event);
    event.currentTarget.setPointerCapture(event.pointerId);
    drawingRef.current = strokesRef.current.length < 200;
    recognitionGenerationRef.current += 1;
    recognitionAbortRef.current?.abort();
    setBusy(false);
    setStrokes((value) => (value.length >= 200 ? value : [...value, [point]]));
    setRecognition(undefined);
  };
  const move = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current) return;
    const point = pointFor(event);
    setStrokes((value) => [
      ...value.slice(0, -1),
      [...(value.at(-1) || []).slice(0, 999), point],
    ]);
  };
  const end = () => {
    drawingRef.current = false;
  };

  const preview = useCallback(async () => {
    const canvas = canvasRef.current;
    if (!canvas || strokesRef.current.length === 0) return;
    setBusy(true);
    const generation = recognitionGenerationRef.current + 1;
    recognitionGenerationRef.current = generation;
    recognitionAbortRef.current?.abort();
    const controller = new AbortController();
    recognitionAbortRef.current = controller;
    setStatus('Reading your handwriting…');
    try {
      const blob = await new Promise<Blob>((resolve, reject) =>
        canvas.toBlob(
          (value) =>
            value
              ? resolve(value)
              : reject(new Error('Could not prepare sketch.')),
          'image/png',
        ),
      );
      const result = await api<Recognition>('/api/recognize-handwriting', {
        method: 'POST',
        body: blob,
        headers: { 'Content-Type': 'image/png' },
        signal: controller.signal,
      });
      if (recognitionGenerationRef.current !== generation) return;
      setRecognition(result);
      setDraft(result.equation);
      setStatus(
        result.equation
          ? 'Check every symbol. Edit anything I misread before using it.'
          : 'I could not read a complete equation. Add clearer spacing or type it below.',
      );
    } catch (error) {
      if (controller.signal.aborted) return;
      setStatus(
        error instanceof Error ? error.message : 'Could not read this sketch.',
      );
    } finally {
      if (recognitionGenerationRef.current === generation) setBusy(false);
    }
  }, []);

  const demoProgress =
    tutorialAudioPositionMs !== undefined && tutorialAudioDurationMs
      ? Math.min(1, tutorialAudioPositionMs / tutorialAudioDurationMs)
      : 1;
  let demoRemaining = demoInk.length * demoProgress;
  let demoTip: readonly [number, number] | undefined;
  for (const stroke of demoInk.strokes) {
    if (demoRemaining >= 0 && demoRemaining <= stroke.length) {
      demoTip = pointAt(stroke.points, demoRemaining);
      break;
    }
    demoRemaining -= stroke.length;
  }
  const tutorial = showTutorial ? (
    <div className="sketch-tutorial" role="note">
      <svg
        viewBox={`0 0 ${demoInk.width + 20} 65`}
        aria-label="Example writing x plus 2 equals 5"
      >
        <g
          transform="translate(10 10)"
          fill="none"
          stroke="#245046"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          {demoInk.strokes.map((stroke, i) => {
            const progress =
              tutorialAudioPositionMs !== undefined && tutorialAudioDurationMs
                ? Math.min(1, tutorialAudioPositionMs / tutorialAudioDurationMs)
                : 1;
            const previous = demoInk.strokes
              .slice(0, i)
              .reduce((sum, s) => sum + s.length, 0);
            const visible = Math.max(
              0,
              Math.min(stroke.length, demoInk.length * progress - previous),
            );
            return (
              <polyline
                key={i}
                points={stroke.points.map((p) => p.join(',')).join(' ')}
                pathLength={stroke.length}
                strokeDasharray={`${visible} ${stroke.length + 1}`}
              />
            );
          })}
        </g>
        {demoTip && demoProgress < 1 && (
          <g
            className="sketch-demo-pencil"
            transform={`translate(${demoTip[0] + 10} ${demoTip[1] + 10}) rotate(-30)`}
            opacity=".7"
            aria-hidden="true"
          >
            <path
              d="M0 0L-3-9H3Z"
              fill="#bd8e56"
              stroke="none"
              style={{ animation: 'none', strokeDasharray: 'none' }}
            />
            <rect x="-3" y="-37" width="6" height="28" rx="1" fill="#c9a657" />
          </g>
        )}
      </svg>
      <p>
        Watch the example. Then write one equation below, preview it, and
        confirm what you meant.
      </p>
      <button
        type="button"
        onClick={() => {
          setShowTutorial(false);
          onTutorialDismiss?.();
        }}
      >
        My turn
      </button>
    </div>
  ) : null;

  return (
    <section
      className={`sketch-pad ${recognition ? 'has-preview' : ''} ${className}`}
      aria-label="Handwriting pad"
    >
      {tutorialPosition === 'above' && tutorial}
      <canvas
        ref={canvasRef}
        className="sketch-canvas"
        aria-label="Draw a math equation"
        onPointerDown={begin}
        onPointerMove={move}
        onPointerUp={end}
        onPointerCancel={end}
      />
      {tutorialPosition === 'inside' && tutorial}
      <div className="sketch-controls">
        <button
          type="button"
          disabled={!strokes.length || busy}
          onClick={() => {
            recognitionGenerationRef.current += 1;
            recognitionAbortRef.current?.abort();
            setStrokes((value) => value.slice(0, -1));
            setRecognition(undefined);
          }}
        >
          Undo stroke
        </button>
        <button
          type="button"
          disabled={!strokes.length || busy}
          onClick={() => {
            recognitionGenerationRef.current += 1;
            recognitionAbortRef.current?.abort();
            setStrokes([]);
            clearStoredSketch(activityKey);
            setRecognition(undefined);
            setDraft('');
            setStatus('Sketch cleared.');
          }}
        >
          Clear
        </button>
        <button
          type="button"
          className="sketch-preview-button"
          disabled={!strokes.length || busy}
          onClick={preview}
        >
          {busy ? 'Reading…' : 'Preview equation'}
        </button>
        <button
          type="button"
          onClick={() => {
            recognitionGenerationRef.current += 1;
            recognitionAbortRef.current?.abort();
            setBusy(false);
            setRecognition({
              equation: '',
              confidence: null,
              provider: 'typed',
              limitations:
                'Enter your equation, then choose Use this equation. Your drawing stays saved.',
            });
            setStatus('You can enter your working with a keyboard.');
            setShowTutorial(false);
            onTutorialDismiss?.();
          }}
        >
          Type instead
        </button>
      </div>
      <p className="sketch-status" aria-live="polite">
        {status}
      </p>
      {recognition && (
        <div ref={previewRef} className="sketch-recognition">
          <label htmlFor={`recognized-${activityKey}`}>
            Digital equation preview
          </label>
          <input
            id={`recognized-${activityKey}`}
            value={draft}
            maxLength={120}
            onChange={(event) => setDraft(event.target.value)}
            placeholder="Type the equation if needed"
            autoComplete="off"
          />
          <p>{recognition.limitations}</p>
          <button
            type="button"
            disabled={!draft.trim()}
            onClick={() => onAcceptedEquation(draft.trim())}
          >
            Use this equation
          </button>
        </div>
      )}
    </section>
  );
}
